// src/components/BulkUpload.tsx

import React, { useState, useEffect, useMemo } from 'react'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import Swal from 'sweetalert2'
import {
  uploadRechargeExcel,
  processRecharges
} from '../services/uploadRechargeExcel'
import {
  uploadPaymentServicesExcel,
  processPaymentServices
} from '../services/uploadPaymentServices'
import { fetchBulkUploadHistory } from '../services/uploadHistory'
import Pagination from '../../products/Pagination'

type TabType = 'history' | 'recharges' | 'paymentServices'

interface HistoryRecord {
  id: number
  documentName: string
  documentPath: string
  userId: number
  userName: string
  status: 'success' | 'failed'
  uploadType: 'recharges' | 'payment_services'
  createdAt: string
}

export const BulkUpload: React.FC = () => {
  // --- pestañas ---
  const [currentTab, setCurrentTab] = useState<TabType>('history')

  // --- recargas / pago de servicios ---
  const [file, setFile] = useState<File | null>(null)
  const [rechargeResults, setRechargeResults] = useState<any[]>([])
  const [paymentResults, setPaymentResults] = useState<any[]>([])

  // --- historial ---
  const [historyRows, setHistoryRows] = useState<HistoryRecord[]>([])
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [pages, setPages] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [filterType, setFilterType] = useState<'recharges' | 'payment_services' | ''>('')
  const [loadingHistory, setLoadingHistory] = useState(false)

  // cargar historial al cambiar filtros, página o pestaña
  useEffect(() => {
    if (currentTab === 'history') {
      loadHistory()
    }
  }, [currentTab, page, limit, startDate, endDate, filterType])

  const loadHistory = async () => {
    setLoadingHistory(true)
    Swal.fire({
      title: 'Cargando historial...',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading() }
    })
    try {
      const resp = await fetchBulkUploadHistory({
        page,
        limit,
        startDate,
        endDate,
        uploadType: filterType || undefined
      })
      // resp: { items: BulkHistoryRow[], total, page, pages }
      const mapped = resp.items.map((r: any) => ({
        id: Number(r.id),
        documentName: r.documentName,
        documentPath: r.downloadUrl || r.documentPath,
        userId: r.userId,
        userName: r.userName,
        status: r.status,
        uploadType: r.uploadType,
        createdAt: r.createdAt
      }))
      setHistoryRows(mapped)
      setTotal(resp.total)
      setPages(resp.pages)
    } catch (err: any) {
      Swal.fire('Error', err.response?.data?.message || 'No se pudo cargar el historial', 'error')
    } finally {
      setLoadingHistory(false)
      Swal.close()
    }
  }

  // manejar selección de archivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFile(e.target.files?.[0] || null)

  // validar Excel
  const handleReview = async () => {
    if (!file) return
    Swal.fire({
      title: 'Validando archivo...',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading() }
    })
    try {
      if (currentTab === 'recharges') {
        const result = await uploadRechargeExcel(file)
        setRechargeResults(result.data)
      } else {
        const result = await uploadPaymentServicesExcel(file)
        setPaymentResults(result.data)
      }
      Swal.close()
      Swal.fire('Éxito', 'Archivo validado correctamente', 'success')
    } catch (err: any) {
      Swal.close()
      const msg = err.response?.data?.message ?? 'Error al validar el archivo'
      Swal.fire('Error', msg, 'error')
    }
  }

  // procesar filas OK
  const handleProcess = async () => {
    Swal.fire({
      title: 'Procesando datos...',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading() }
    })
    try {
      if (currentTab === 'recharges') {
        await processRecharges(rechargeResults)
        setRechargeResults([])
      } else {
        await processPaymentServices(paymentResults)
        setPaymentResults([])
      }
      setFile(null)
      Swal.close()
      Swal.fire('Registrado', 'Proceso completado correctamente', 'success')
    } catch (err: any) {
      Swal.close()
      const msg = err.response?.data?.message ?? 'Error al procesar los datos'
      Swal.fire('Error', msg, 'error')
    }
  }

  const canProcess = useMemo(() => {
    const arr = currentTab === 'recharges' ? rechargeResults : paymentResults
    return arr.length > 0 && arr.every(r => r.result === 'OK')
  }, [currentTab, rechargeResults, paymentResults])

  const getLayoutLink = () =>
    currentTab === 'recharges'
      ? '/media/layouts/layout-recargas.xlsx'
      : '/media/layouts/layout-pago-servicios.xlsx'

  const getReviewButtonText = () =>
    currentTab === 'recharges'
      ? 'Revisar layout de recargas'
      : 'Revisar layout de pago de servicios'

  // --- sección de carga de archivo ---
  const renderFileSection = () => (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label mb-0">Seleccionar archivo</label>
        <a
          href={toAbsoluteUrl(getLayoutLink())}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary text-decoration-underline d-flex align-items-center gap-2"
        >
          Descargar layout
          <i className="bi bi-download" />
        </a>
      </div>
      <div className="row align-items-center">
        <div className="col-md-4">
          <input type="file" className="form-control" onChange={handleFileChange} />
        </div>
        <div className="col d-flex justify-content-end">
          <button className="btn btn-primary" onClick={handleReview} disabled={!file}>
            {getReviewButtonText()}
          </button>
        </div>
      </div>
    </div>
  )

  // --- filtros de historial ---
  const renderHistoryFilters = () => (
    <div className="row g-3 mb-4 align-items-end">
      <div className="col-md-3">
        <label>Fecha inicio</label>
        <input
          type="date"
          className="form-control"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
        />
      </div>
      <div className="col-md-3">
        <label>Fecha fin</label>
        <input
          type="date"
          className="form-control"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
        />
      </div>
      <div className="col-md-3">
        <label>Tipo de carga</label>
        <select
          className="form-select"
          value={filterType}
          onChange={e => setFilterType(e.target.value as any)}
        >
          <option value="">Todos</option>
          <option value="recharges">Recargas</option>
          <option value="payment_services">Pago de servicios</option>
        </select>
      </div>
      <div className="col-md-3 d-flex gap-2">
        <button className='btn btn-primary rounded-pill' onClick={() => { setPage(1); loadHistory() }}>
          Buscar
        </button>
        <button className='btn btn-secondary rounded-pill' onClick={() => {
          setStartDate(''); setEndDate(''); setFilterType(''); setPage(1)
        }}>
          Borrar
        </button>
      </div>
    </div>
  )

  // --- tabla de historial con distribución uniforme ---
  const renderHistoryTable = () => (
    <div className="table-responsive">
      <table
        className="table table-striped align-middle"
        style={{ tableLayout: 'fixed', width: '100%' }}
      >
        <thead>
          <tr>
            <th style={{ width: '5%' }}>#</th>
            <th style={{ width: '20%' }}>Nombre</th>
            <th style={{ width: '25%' }}>Usuario</th>
            <th style={{ width: '25%' }}>Fecha de creación</th>
            <th style={{ width: '12%' }}>Estatus</th>
            <th style={{ width: '13%' }}>Tipo de carga</th>
          </tr>
        </thead>
        <tbody>
          {loadingHistory ? (
            <tr><td colSpan={6} className="text-center">Cargando...</td></tr>
          ) : historyRows.length === 0 ? (
            <tr><td colSpan={6} className="text-center">No hay registros</td></tr>
          ) : historyRows.map((r, i) => (
            <tr key={r.id}>
              <td>{i + 1}</td>
              <td className="text-truncate" title={r.documentName}>
                <a
                  href={r.documentPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-block text-truncate"
                  style={{ maxWidth: '100%' }}
                >
                  {r.documentName || `Archivo #${r.id}`}
                </a>
              </td>
              <td className="text-truncate">{r.userName}</td>
              <td>{new Date(r.createdAt).toLocaleString()}</td>
              <td>{r.status === 'success' ? 'Exitosa' : 'Fallida'}</td>
              <td>{r.uploadType === 'recharges' ? 'Recargas' : 'Pago de servicios'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Paginación */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <select
          className="form-select form-select-sm w-auto"
          value={limit}
          onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
        >
          {[10, 20, 50].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <Pagination
          currentPage={page}
          setCurrentPage={setPage}
          totalPages={pages}
          limit={limit}
          lengthData={historyRows.length}
        />
      </div>
    </div>
  )

  // --- tabla de resultados recargas / pago ---
  const renderResultTable = (rows: any[]) => (
    <div className="table-responsive">
      <table className="table align-middle table-row-dashed fs-6 gy-5">
        <thead>
          <tr className="text-start text-muted fw-bold text-uppercase gs-0">
            {currentTab === 'recharges' ? (
              <>
                <th>#</th><th>Acción</th><th>Operador</th><th>SKU</th>
                <th>Denominación</th><th>Descuento Total</th>
                <th>Descuento EQ</th><th>Descuento cliente</th><th>Resultado</th>
              </>
            ) : (
              <>
                <th>#</th><th>Acción</th><th>SKU</th><th>Categoría</th>
                <th>Proveedor</th><th>Tipo conexión</th><th>Req. conexión</th>
                <th>Precio final</th><th>Monto promedio</th><th>Área cobertura</th>
                <th>Comisión total</th><th>Comisión Datalogic</th><th>Comisión EQ</th>
                <th>Comisión Cliente</th><th>Resultado</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={currentTab === 'recharges' ? 9 : 15} className="text-center">
                No hay resultados
              </td>
            </tr>
          ) : rows.map((r, i) => (
            <tr key={i} className={r.result !== 'OK' ? 'table-danger' : ''}>
              <td>{i + 1}</td>
              <td>{r.action}</td>
              {currentTab === 'recharges' ? (
                <>
                  <td>{r.operator}</td><td>{r.SKU}</td><td>{r.denomination}</td>
                  <td>{r.total_discount}</td><td>{r.equality_discount}</td>
                  <td>{r.client_discount}</td><td>{r.result}</td>
                </>
              ) : (
                <>
                  <td>{r.SKU}</td><td>{r.category}</td><td>{r.provider}</td>
                  <td>{r.connectionType}</td><td>{r.connectionRequirements}</td>
                  <td>{r.finalPrice}</td><td>{r.averageAmount}</td>
                  <td>{r.coverageArea}</td><td>{r.totalCommission}</td>
                  <td>{r.datalogicCommission}</td><td>{r.equalityCommission}</td>
                  <td>{r.customerCommission}</td><td>{r.result}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="card p-6 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="card-title m-0">Listado de cargas masivas</h3>
      </div>
      <div className="d-flex flex-column flex-md-row">
        <nav className="nav flex-column flex-shrink-0 w-25 mb-4 mb-md-0">
          {[
            { id: 'history', label: 'Historial' },
            { id: 'recharges', label: 'Recargas' },
            { id: 'paymentServices', label: 'Pago de Servicios' }
          ].map(tab => (
            <div
              key={tab.id}
              onClick={() => {
                setFile(null)
                setRechargeResults([])
                setPaymentResults([])
                setCurrentTab(tab.id as TabType)
              }}
              className={`cursor-pointer py-3 px-2 ${currentTab === tab.id ? 'fw-bold ' : 'text-dark'}`}
              style={{
                borderBottom: currentTab === tab.id ? '2px solid var(--kt-primary)' : ''
              }}
            >
              {tab.label}
            </div>
          ))}
        </nav>
        <div className="flex-grow-1 ps-md-5">
          {currentTab === 'history' && (
            <>
              {renderHistoryFilters()}
              {renderHistoryTable()}
            </>
          )}
          {currentTab === 'recharges' && (
            <>
              {renderFileSection()}
              {renderResultTable(rechargeResults)}
              <div className="d-flex justify-content-end mt-4">
                <button
                  className="btn btn-primary"
                  disabled={!canProcess}
                  onClick={handleProcess}
                >
                  Registrar
                </button>
              </div>
            </>
          )}
          {currentTab === 'paymentServices' && (
            <>
              {renderFileSection()}
              {renderResultTable(paymentResults)}
              <div className="d-flex justify-content-end mt-4">
                <button
                  className="btn btn-primary"
                  disabled={!canProcess}
                  onClick={handleProcess}
                >
                  Registrar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
